// src/components/games/pipsWordPuzzle/PipsWordPuzzleArchive.tsx
import { logger } from '../../../utils/logger';
// FIXED: Removed emojis to prevent encoding corruption

import React, { useState, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  IonList,
  IonItem,
  IonLabel,
  IonIcon,
  IonBadge,
  IonCard,
  IonCardContent,
  IonButton,
  IonSearchbar,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonSpinner,
  useIonViewWillEnter,
} from '@ionic/react';
import { calendar, lockClosed, sparkles, checkmarkCircle } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { DailyPuzzle } from './types';
import { supabase } from '../../../services/supabase';
import { getCompletedPuzzles } from '../../../services/pipsWordPuzzleStorageService';
import { usePremium } from '../../../hooks/usePremium';
import { PremiumPaywall } from '../../../components/PremiumPaywall';
import './PipsWordPuzzle.css';

const FREE_ARCHIVE_LIMIT = 3;
const ITEMS_PER_PAGE = 20;

export const PipsWordPuzzleArchive: React.FC = () => {
  const history = useHistory();
  const { isPremium } = usePremium();
  const [archivePuzzles, setArchivePuzzles] = useState<DailyPuzzle[]>([]);
  const [filteredPuzzles, setFilteredPuzzles] = useState<DailyPuzzle[]>([]);
  const [displayedPuzzles, setDisplayedPuzzles] = useState<DailyPuzzle[]>([]);
  const [completedPuzzles, setCompletedPuzzles] = useState<string[]>([]);
  const [showPaywall, setShowPaywall] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);

  // Refresh the archive every time the page comes into view
  useIonViewWillEnter(() => {
    logger.log('Archive page entering view - refreshing data');
    loadAllArchive();
    loadCompletedPuzzles();
    // Force re-render to update completion status
    setRefreshKey(prev => prev + 1);
  });

  // Load completed puzzles
  const loadCompletedPuzzles = async () => {
    const completed = await getCompletedPuzzles();
    setCompletedPuzzles(completed);
  };

  useEffect(() => {
    // Filter puzzles based on search
    if (searchText.trim() === '') {
      setFilteredPuzzles(archivePuzzles);
    } else {
      const filtered = archivePuzzles.filter(puzzle =>
        puzzle.date.includes(searchText) ||
        puzzle.category.toLowerCase().includes(searchText.toLowerCase()) ||
        puzzle.word.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredPuzzles(filtered);
    }
    setCurrentPage(1);
  }, [searchText, archivePuzzles]);

  useEffect(() => {
    // Update displayed puzzles based on current page
    setDisplayedPuzzles(filteredPuzzles.slice(0, currentPage * ITEMS_PER_PAGE));
  }, [filteredPuzzles, currentPage]);

  const loadAllArchive = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

      // Fetch ONLY past puzzles (archived), ordered by date_used descending
      const { data, error } = await supabase
        .from('nerdle_puzzles')
        .select('*')
        .lt('date_used', today) // Less than today (past puzzles only)
        .not('date_used', 'is', null) // Exclude null dates
        .order('date_used', { ascending: false });

      if (error) {
        logger.error('Error fetching archive:', error);
        setArchivePuzzles([]);
        setFilteredPuzzles([]);
        setLoading(false);
        return;
      }

      if (!data || data.length === 0) {
        logger.log('No archived puzzles found');
        setArchivePuzzles([]);
        setFilteredPuzzles([]);
        setLoading(false);
        return;
      }

      logger.log(`Found ${data.length} archived puzzles`);

      const puzzles: DailyPuzzle[] = data.map(puzzle => ({
        word: puzzle.word,
        date: puzzle.date_used, // Use date_used from database
        category: puzzle.category,
        hints: [
          { id: 1, text: puzzle.hint1, cost: 5, revealed: false },
          { id: 2, text: puzzle.hint2, cost: 5, revealed: false },
          { id: 3, text: puzzle.hint3, cost: 5, revealed: false }
        ]
      }));

      setArchivePuzzles(puzzles);
      setFilteredPuzzles(puzzles);
    } catch (err) {
      logger.error('Exception fetching archive:', err);
      setArchivePuzzles([]);
      setFilteredPuzzles([]);
    }
    setLoading(false);
  };

  const handlePuzzleSelect = (puzzle: DailyPuzzle, index: number) => {
    // Check if premium required
    if (!isPremium && index >= FREE_ARCHIVE_LIMIT) {
      setShowPaywall(true);
      return;
    }

    // Navigate to the game with the selected puzzle
    history.push('/games/pips-word-puzzle/play', { puzzle, fromArchive: true });
  };

  const loadMorePuzzles = (e: CustomEvent<void>) => {
    setCurrentPage(prev => prev + 1);
    (e.target as HTMLIonInfiniteScrollElement).complete();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00'); // Add time to avoid timezone issues
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/games/pips-word-puzzle" />
          </IonButtons>
          <IonTitle>Puzzle Archive</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div className="archive-container">
          {loading ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
              gap: '12px'
            }}>
              <IonSpinner name="crescent" />
              <div style={{ fontSize: '16px', color: 'var(--ion-color-medium)' }}>
                Loading archive...
              </div>
            </div>
          ) : (
            <>
              {/* Info Card */}
              <IonCard>
                <IonCardContent>
                  <p style={{ fontSize: '14px', margin: 0, textAlign: 'center' }}>
                    {isPremium ? (
                      <>
                        <IonIcon icon={sparkles} style={{ marginRight: '4px', color: 'var(--ion-color-success)' }} />
                        <strong>Premium Member:</strong> Access all {archivePuzzles.length} archived puzzles!
                      </>
                    ) : (
                      <>
                        <strong>Free Access:</strong> Play {FREE_ARCHIVE_LIMIT} archived puzzles
                        <br />
                        <IonIcon icon={sparkles} style={{ marginRight: '4px', color: 'var(--ion-color-warning)' }} />
                        Upgrade for unlimited archive access ({archivePuzzles.length} total puzzles)
                      </>
                    )}
                  </p>
                </IonCardContent>
              </IonCard>

              {/* Search Bar */}
              <IonSearchbar
                value={searchText}
                onIonInput={(e) => setSearchText(e.detail.value || '')}
                placeholder="Search by date, category, or word..."
                debounce={300}
                style={{ padding: '0 0 12px 0' }}
              />

              {/* Results Count */}
              <div style={{
                textAlign: 'center',
                fontSize: '13px',
                color: 'var(--ion-color-medium)',
                marginBottom: '12px'
              }}>
                {searchText ? (
                  <>Showing {filteredPuzzles.length} of {archivePuzzles.length} puzzles</>
                ) : (
                  <>{archivePuzzles.length} puzzles available</>
                )}
              </div>

              {/* Archive List */}
              {displayedPuzzles.length === 0 ? (
                <IonCard>
                  <IonCardContent style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>[SEARCH]</div>
                    <p style={{ fontSize: '16px', color: 'var(--ion-color-medium)', margin: 0 }}>
                      {searchText ? 'No puzzles found matching your search' : 'No archived puzzles available'}
                    </p>
                    {!searchText && archivePuzzles.length === 0 && (
                      <p style={{ fontSize: '14px', color: 'var(--ion-color-medium)', marginTop: '12px' }}>
                        Complete today's puzzle to start building your archive!
                      </p>
                    )}
                  </IonCardContent>
                </IonCard>
              ) : (
                <>
                  <IonList key={refreshKey}>
                    {displayedPuzzles.map((puzzle, index) => {
                      const isLocked = !isPremium && index >= FREE_ARCHIVE_LIMIT;
                      // Check if puzzle is in completed list
                      const key = `${puzzle.date}:${puzzle.word}`;
                      const isCompleted = completedPuzzles.includes(key);
                      return (
                        <IonItem
                          key={`${puzzle.date}-${puzzle.word}-${refreshKey}`}
                          button={!isLocked}
                          onClick={() => handlePuzzleSelect(puzzle, index)}
                          className={isLocked ? 'archive-item-locked' : ''}
                          style={isCompleted ? { opacity: 0.7 } : {}}
                        >
                          <IonIcon
                            icon={isCompleted ? checkmarkCircle : calendar}
                            slot="start"
                            color={isLocked ? 'medium' : isCompleted ? 'success' : 'primary'}
                          />
                          <IonLabel>
                            <h3 style={isCompleted ? { textDecoration: 'line-through' } : {}}>
                              {formatDate(puzzle.date)}
                            </h3>
                            <p>
                              <strong>{puzzle.category}</strong>
                              {searchText && !searchText.match(/^\d{4}-\d{2}-\d{2}$/) && (
                                <> • {puzzle.word}</>
                              )}
                            </p>
                          </IonLabel>
                          {isCompleted && !isLocked && (
                            <IonBadge color="success" slot="end" style={{ marginRight: '8px' }}>
                              ✓ Done
                            </IonBadge>
                          )}
                          {isLocked && (
                            <IonBadge color="warning" slot="end">
                              <IonIcon icon={lockClosed} style={{ marginRight: '4px' }} />
                              Premium
                            </IonBadge>
                          )}
                        </IonItem>
                      );
                    })}
                  </IonList>

                  {/* Infinite Scroll */}
                  {displayedPuzzles.length < filteredPuzzles.length && (
                    <IonInfiniteScroll
                      onIonInfinite={loadMorePuzzles}
                      threshold="100px"
                    >
                      <IonInfiniteScrollContent
                        loadingSpinner="bubbles"
                        loadingText="Loading more puzzles..."
                      />
                    </IonInfiniteScroll>
                  )}
                </>
              )}

              {/* Premium Upsell */}
              {!isPremium && displayedPuzzles.length > 0 && (
                <IonCard style={{ marginTop: '16px' }}>
                  <IonCardContent style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '14px', marginBottom: '12px', color: 'var(--ion-color-medium)' }}>
                      Get unlimited access to all archived puzzles
                    </div>
                    <IonButton
                      expand="block"
                      color="warning"
                      onClick={() => setShowPaywall(true)}
                    >
                      <IonIcon icon={sparkles} slot="start" />
                      Unlock All {archivePuzzles.length} Puzzles
                    </IonButton>
                  </IonCardContent>
                </IonCard>
              )}
            </>
          )}
        </div>

        {/* Premium Paywall */}
        <PremiumPaywall
          isOpen={showPaywall}
          onDismiss={() => setShowPaywall(false)}
          feature="nerdle_archive"
        />
      </IonContent>
    </IonPage>
  );
};